/**
 * PDF Generation Utilities
 * 
 * Generate PDFs for place cards, booking forms, and attendance lists
 * Uses jsPDF and html2canvas for rendering
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { RSVP, Event, Invoice, PlaceCardTemplate, PlaceCardField, PlaceCardElement, PlaceCardFrame, Rank, UnitType } from '@/types';
import { applyPlaceholders, buildPlaceCardSeatingMap, buildPlaceholderMap } from '@/lib/place-card-placeholders';
import { cssFontWeight, ensurePlaceCardFontsLoaded } from '@/lib/place-card-fonts';
import { cardsThatFit, templateCardSizeInches } from '@/lib/place-card-layout';
import { getAttendanceExportAmounts, getAttendanceExportPaymentStatusLabel } from '@/lib/attendance-payment-state';
import { formatRsvpQuestionAnswers, getRsvpQuestionAnswers } from '@/lib/rsvp-question-answers';
import { formatCurrency } from '@/lib/utils';
import {
  normalizeTicketCoursesToFormatA,
  getSelectionFromFormatA,
} from '@/lib/ticket-courses';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { buildSpiralPoints, buildWavyRectPoints, smoothSvgPathFromPoints } from '@/lib/place-card-frame-geometry';
import type { VenueDetails } from '@/components/kitchen-report/KitchenReportModal';
import { sortBookingFormGuestsBySeatAsc } from '@/lib/booking-form-pdf-sort';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const CSS_DPI = 96;
const TARGET_DPI = 300; // Print-quality target DPI

type SeatingTable = {
  id: string;
  name?: string;
  type: string;
  seats: number;
  seating: Array<{
    seatNumber: number;
    rsvpId?: string | null;
    role?: string | null;
  }>;
  position?: {
    x: number;
    y: number;
  };
};

function sortRsvpsBySeating(rsvps: RSVP[], tables: SeatingTable[]): RSVP[] {
  if (!tables || tables.length === 0) {
    return rsvps;
  }

  const rsvpMap = new Map<string, RSVP>();
  const seen = new Set<string>();
  const ordered: RSVP[] = [];

  rsvps.forEach((rsvp) => {
    if (rsvp.id) {
      rsvpMap.set(rsvp.id, rsvp);
    }
  });

  tables.forEach((table) => {
    const sortedSeats = [...table.seating].sort(
      (a, b) => a.seatNumber - b.seatNumber,
    );

    sortedSeats.forEach((seat) => {
      if (!seat.rsvpId) {
        return;
      }

      const mappedRsvp = rsvpMap.get(seat.rsvpId);

      if (mappedRsvp && mappedRsvp.id && !seen.has(mappedRsvp.id)) {
        ordered.push(mappedRsvp);
        seen.add(mappedRsvp.id);
      }
    });
  });

  rsvps.forEach((rsvp) => {
    if (rsvp.id && !seen.has(rsvp.id)) {
      ordered.push(rsvp);
      seen.add(rsvp.id);
    }
  });

  return ordered;
}

/**
 * Generate Place Cards PDF
 * Creates printable place cards for all guests
 */
export async function generatePlaceCards(
  rsvps: RSVP[],
  event: Event,
  tables: SeatingTable[]
): Promise<void> {
  if (!rsvps || rsvps.length === 0) {
    throw new Error('No RSVPs provided for place cards generation');
  }
  
  if (!event) {
    throw new Error('No event provided for place cards generation');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Place card dimensions (2 per page)
  const cardWidth = pageWidth - 20;
  const cardHeight = (pageHeight / 2) - 20;
  
  let cardCount = 0;
  
  // Create map of RSVP to table/seat
  const seatingMap = new Map<string, { tableName: string; seatNumber: number }>();
  tables.forEach((table) => {
    table.seating.forEach((seat) => {
      const seatRsvpId = seat.rsvpId ?? undefined;
      if (seatRsvpId) {
        seatingMap.set(seatRsvpId, {
          tableName: getTableNameFromTable(table),
          seatNumber: seat.seatNumber,
        });
      }
    });
  });

  const orderedRsvps = sortRsvpsBySeating(rsvps, tables);

  orderedRsvps.forEach((rsvp) => {
    if (cardCount > 0 && cardCount % 2 === 0) {
      doc.addPage();
    }

    const yOffset = (cardCount % 2) * (cardHeight + 10) + 10;
    const seating = seatingMap.get(rsvp.id);

    // Draw card border
    doc.setDrawColor(30, 58, 95); // Navy blue
    doc.setLineWidth(0.5);
    doc.rect(10, yOffset, cardWidth, cardHeight);

    // Title (Event name)
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(event.name, pageWidth / 2, yOffset + 15, { align: 'center' });

    // Guest name (large) with text wrapping
    doc.setFontSize(24);
    doc.setTextColor(30, 58, 95); // Navy
    
    // Split long names into multiple lines if needed
    const maxWidth = cardWidth - 20; // Leave some margin
    const nameLines = doc.splitTextToSize(rsvp.name, maxWidth);
    const lineHeight = 8;
    const startY = yOffset + 35 - (nameLines.length - 1) * lineHeight / 2;
    
    nameLines.forEach((line: string, index: number) => {
      doc.text(line, pageWidth / 2, startY + index * lineHeight, { align: 'center' });
    });

    // Rank prefix - prefer short form if available
    const rankPrefix = rsvp.rankShortName ?? rsvp.rank ?? '';
    if (rankPrefix) {
      doc.setFontSize(14);
      doc.setTextColor(100, 100, 100);
      doc.text(rankPrefix, pageWidth / 2, yOffset + 45, { align: 'center' });
    }

    // Unit
    if (rsvp.unit) {
      doc.setFontSize(10);
      doc.text(rsvp.unit, pageWidth / 2, yOffset + 53, { align: 'center' });
    }

    // Table and Seat
    if (seating) {
      doc.setFontSize(12);
      doc.setTextColor(212, 175, 55); // Gold
      doc.text(
        `${seating.tableName} • Seat ${seating.seatNumber}`,
        pageWidth / 2,
        yOffset + cardHeight - 15,
        { align: 'center' }
      );
    }

    // Meal/Course choice (if specified)
    let mealText = '';
    if (event.courses && event.courses.length > 0) {
      // Show primary course (usually main course)
      const mainCourse = event.courses.find((c) => 
        c.name.toLowerCase().includes('main')
      ) || event.courses[0];
      if (rsvp.responses?.[mainCourse.name]) {
        mealText = rsvp.responses[mainCourse.name];
      }
    } else if (rsvp.responses?.Meal) {
      mealText = rsvp.responses.Meal;
    }
    
    if (mealText) {
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Meal: ${mealText}`,
        pageWidth / 2,
        yOffset + cardHeight - 8,
        { align: 'center' }
      );
    }

    cardCount++;
  });

  doc.save(`${event.name} - Place Cards.pdf`);
}

interface RoomTablePlan {
  id: string;
  name: string;
  tables: SeatingTable[];
}

interface TableAssignment {
  roomName: string;
  tableName: string;
  seatNumber: number;
}

/**
 * Generate Booking Form PDF
 * Comprehensive report with course summary, guest details, and table plans
 */
export async function generateKitchenReport(
  rsvps: RSVP[],
  event: Event,
  roomTablePlans?: RoomTablePlan[],
  venueDetails?: VenueDetails
): Promise<void> {
  if (!rsvps || rsvps.length === 0) {
    throw new Error('No RSVPs provided for booking form generation');
  }
  
  if (!event) {
    throw new Error('No event provided for booking form generation');
  }

  const doc = new jsPDF();
  let yPos = 20;

  // Build table assignment mapping
  const tableAssignments = new Map<string, TableAssignment>();
  if (roomTablePlans && roomTablePlans.length > 0) {
    roomTablePlans.forEach((room) => {
      room.tables.forEach((table) => {
        table.seating.forEach((seat) => {
          if (seat.rsvpId) {
            tableAssignments.set(seat.rsvpId, {
              roomName: room.name,
              tableName: table.name || `Table ${table.id}`,
              seatNumber: seat.seatNumber,
            });
          }
        });
      });
    });
  }

  // ===== PAGE 1: TITLE & SUMMARY =====
  // Header background
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, 210, 50, 'F');
  
  // Title
  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('BOOKING FORM', 105, 25, { align: 'center' });
  
  // Event name
  doc.setFontSize(16);
  doc.setTextColor(200, 200, 200);
  doc.setFont('helvetica', 'normal');
  doc.text(event.name, 105, 40, { align: 'center' });
  
  yPos = 60;

  // Date and time
  doc.setFontSize(12);
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.text(
    event.date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    105,
    yPos,
    { align: 'center' }
  );
  yPos += 8;

  // Location
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(event.location, 105, yPos, { align: 'center' });
  yPos += 20;

  // ===== VENUE DETAILS SECTION =====
  if (venueDetails) {
    // Venue Details Header
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPos - 5, 190, 12, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.rect(10, yPos - 5, 190, 12);
    
    doc.setTextColor(30, 58, 95);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('VENUE DETAILS', 15, yPos + 3);
    yPos += 15;

    // Event Details Table
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPos - 2, 190, 8, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.rect(10, yPos - 2, 190, 8);
    
    // Table header
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    doc.text('Field', 20, yPos + 2);
    doc.text('Value', 100, yPos + 2);
    yPos += 10;

    // Event details rows
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    const eventDetails = [
      ['Lodge/Chapter', venueDetails.lodgeChapter || 'N/A'],
      ['Date of Meeting', venueDetails.dateOfMeeting || 'N/A'],
      ['Dining Organiser', venueDetails.diningOrganiser || 'N/A'],
      ['Contact Telephone', venueDetails.contactTelephone || 'N/A'],
      ['Contact Email', venueDetails.contactEmail || 'N/A'],
      ['Ceremony Start Time', venueDetails.ceremonyStartTime || 'N/A'],
      ['Installation', venueDetails.installation ? 'Yes' : 'No'],
      ['Election', venueDetails.election ? 'Yes' : 'No'],
      ['Preferred Dining Time', venueDetails.preferredDiningTime || 'N/A'],
      ['Preferred Dining Room', venueDetails.preferredDiningRoom || 'N/A'],
      ['Numbers Dining', venueDetails.numbersDining?.toString() || 'N/A'],
    ];

    eventDetails.forEach(([field, value], index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Alternating row background
      if (index % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(10, yPos - 2, 190, 6, 'F');
      }
      
      doc.text(field, 20, yPos);
      doc.text(value, 100, yPos);
      yPos += 6;
    });

    yPos += 10;

    // Meal Selection Details
    if (venueDetails.vegetarianVeganAlternative > 0 || 
        Object.values(venueDetails.dessertSelections || {}).some((v: any) => v > 0) ||
        venueDetails.dietaryRequirements) {
      
      doc.setFillColor(248, 250, 252);
      doc.rect(10, yPos - 5, 190, 12, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(1);
      doc.rect(10, yPos - 5, 190, 12);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(30, 58, 95);
      doc.text('MEAL SELECTION DETAILS', 15, yPos + 3);
      yPos += 15;

      // Vegetarian/Vegan count
      if (venueDetails.vegetarianVeganAlternative > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`Vegetarian/Vegan Alternative: ${venueDetails.vegetarianVeganAlternative}`, 20, yPos);
        yPos += 6;
      }

      // Dessert selections - dynamically populated from event
      if (venueDetails.dessertSelections) {
        Object.entries(venueDetails.dessertSelections).forEach(([name, count]) => {
          const countValue = count as number;
          if (countValue > 0) {
            doc.text(`${name}: ${countValue}`, 20, yPos);
            yPos += 6;
          }
        });
      }

      // A la carte menu
      if (venueDetails.aLaCarteMenu) {
        const aLaCarte = venueDetails.aLaCarteMenu;
        if (aLaCarte.starter || aLaCarte.mainCourse || aLaCarte.dessert) {
          doc.text('A la carte menu:', 20, yPos);
          yPos += 6;
          if (aLaCarte.starter) {
            doc.text(`  Starter: ${aLaCarte.starter}`, 25, yPos);
            yPos += 6;
          }
          if (aLaCarte.mainCourse) {
            doc.text(`  Main Course: ${aLaCarte.mainCourse}`, 25, yPos);
            yPos += 6;
          }
          if (aLaCarte.dessert) {
            doc.text(`  Dessert: ${aLaCarte.dessert}`, 25, yPos);
            yPos += 6;
          }
        }
      }

      // Dietary requirements
      if (venueDetails.dietaryRequirements) {
        doc.text(`Dietary Requirements: ${venueDetails.dietaryRequirements}`, 20, yPos);
        yPos += 6;
      }

      yPos += 10;
    }

    // Venue Setup Details
    if (venueDetails.roomLayout || venueDetails.candlesCandelabras || 
        venueDetails.linenTablecloths || venueDetails.linenNapkins || 
        venueDetails.gunRoom || venueDetails.barTabRequired || 
        venueDetails.additionalItems) {
      
      doc.setFillColor(248, 250, 252);
      doc.rect(10, yPos - 5, 190, 12, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(1);
      doc.rect(10, yPos - 5, 190, 12);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(30, 58, 95);
      doc.text('VENUE SETUP', 15, yPos + 3);
      yPos += 15;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      if (venueDetails.roomLayout) {
        doc.text(`Room Layout: ${venueDetails.roomLayout}`, 20, yPos);
        yPos += 6;
      }

      const setupItems = [
        ['Candles/Candelabras', venueDetails.candlesCandelabras],
        ['Linen Tablecloths', venueDetails.linenTablecloths],
        ['Linen Napkins', venueDetails.linenNapkins],
        ['Gun Room', venueDetails.gunRoom],
        ['Bar Tab Required', venueDetails.barTabRequired],
      ];

      setupItems.forEach(([item, enabled]) => {
        if (enabled) {
          doc.text(`✓ ${item}`, 20, yPos);
          yPos += 6;
        }
      });

      if (venueDetails.barTabContact) {
        doc.text(`Bar Tab Contact: ${venueDetails.barTabContact}`, 20, yPos);
        yPos += 6;
      }

      if (venueDetails.additionalItems) {
        doc.text(`Additional Items: ${venueDetails.additionalItems}`, 20, yPos);
        yPos += 6;
      }

      yPos += 10;
    }
  }

  // Quick Stats Box
  doc.setFillColor(248, 250, 252);
  doc.rect(10, yPos, 190, 30, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.rect(10, yPos, 190, 30);
  
  // Stats header
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.text('EVENT SUMMARY', 15, yPos + 8);
  
  // Calculate table statistics
  let totalTables = 0;
  let totalSeats = 0;
  const tableSeatCounts: number[] = [];
  
  if (roomTablePlans && roomTablePlans.length > 0) {
    roomTablePlans.forEach((room) => {
      room.tables.forEach((table) => {
        totalTables++;
        totalSeats += table.seats;
        tableSeatCounts.push(table.seats);
      });
    });
  }

  // Stats content
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Guests: ${rsvps.length}`, 15, yPos + 15);
  
  if (totalTables > 0) {
    doc.text(`Tables: ${totalTables}`, 80, yPos + 15);
    doc.text(`Total Seats: ${totalSeats}`, 130, yPos + 15);
  } else {
    doc.text(`Tables: Not configured`, 80, yPos + 15);
    doc.text(`Seats: N/A`, 130, yPos + 15);
  }
  
  const allergiesCount = rsvps.filter((r) => r.responses?.Allergies).length;
  doc.setTextColor(239, 68, 68);
  doc.setFont('helvetica', 'bold');
  doc.text(`⚠ Allergies: ${allergiesCount}`, 15, yPos + 25);
  
  const dietaryCount = rsvps.filter((r) => r.responses?.Dietary && Array.isArray(r.responses.Dietary) && r.responses.Dietary.length > 0).length;
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'normal');
  doc.text(`Special Dietary: ${dietaryCount}`, 80, yPos + 25);
  
  // Show table seat breakdown if available
  if (tableSeatCounts.length > 0) {
    const seatBreakdown = tableSeatCounts.sort((a, b) => b - a).slice(0, 3).join(', ');
    const remaining = tableSeatCounts.length - 3;
    const displayText = remaining > 0 ? `${seatBreakdown} +${remaining} more` : seatBreakdown;
    doc.text(`Seats per table: ${displayText}`, 130, yPos + 25);
  }
  
  yPos += 40;

  // ===== CRITICAL ALLERGIES SECTION =====
  const allergies = rsvps.filter((r) => r.responses?.Allergies);
  if (allergies.length > 0) {
    // Allergy header with gradient effect
    doc.setFillColor(239, 68, 68);
    doc.rect(10, yPos, 190, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('⚠️ CRITICAL ALLERGIES - MUST READ', 105, yPos + 8, { align: 'center' });
    yPos += 18;

    // Allergy content with better formatting
    doc.setTextColor(239, 68, 68);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    allergies.forEach((rsvp) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Guest info box
      doc.setFillColor(254, 242, 242);
      doc.rect(10, yPos - 2, 190, 12, 'F');
      doc.setDrawColor(252, 165, 165);
      doc.setLineWidth(0.5);
      doc.rect(10, yPos - 2, 190, 12);
      
      const guestName = `${rsvp.firstName || ''} ${rsvp.lastName || rsvp.name}`.trim();
      const assignment = tableAssignments.get(rsvp.id);
      const location = assignment 
        ? `[${assignment.roomName} - ${assignment.tableName} - Seat ${assignment.seatNumber}]`
        : '[Not seated]';
      
      doc.setTextColor(239, 68, 68);
      doc.setFont('helvetica', 'bold');
      doc.text(`• ${guestName} ${location}:`, 15, yPos + 3);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(`  ${rsvp.responses.Allergies}`, 17, yPos + 3);
      yPos += 8;
    });

    yPos += 10;
  }

  // ===== COURSE SUMMARY TABLE =====
  // Always start on a new page
  doc.addPage();
  yPos = 20;

  // Section header with better styling
  doc.setFillColor(248, 250, 252);
  doc.rect(10, yPos - 5, 190, 12, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.rect(10, yPos - 5, 190, 12);
  
  doc.setTextColor(30, 58, 95);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('COURSE SUMMARY', 15, yPos + 3);
  yPos += 15;

  // Check if event uses ticket-based course selection
  if (event.tickets && event.tickets.length > 0) {
    // New ticket-based structure: group by ticket, then show courses per ticket
    event.tickets.forEach((ticket) => {
      if (!ticket.courses || ticket.courses.length === 0) {
        return; // Skip tickets without courses
      }

      // Ticket header
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(30, 58, 95);
      doc.rect(10, yPos - 3, 190, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(`${ticket.name.toUpperCase()} - COURSE SELECTIONS`, 15, yPos + 3);
      yPos += 12;

      // Process each course for this ticket
      ticket.courses.forEach((course) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        // Course sub-header
        doc.setFillColor(240, 245, 250);
        doc.rect(10, yPos - 3, 190, 8, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.5);
        doc.rect(10, yPos - 3, 190, 8);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(30, 58, 95);
        doc.text(course.name, 15, yPos + 2);
        yPos += 10;

        // Count selections for this course (only for RSVPs with this ticket)
        const courseCounts = new Map<string, number>();
        rsvps.forEach((rsvp) => {
          const rsvpTickets = rsvp.responses?.Tickets;
          if (Array.isArray(rsvpTickets) && rsvpTickets.some((t: any) => t && t.id === ticket.id)) {
            const normalized = normalizeTicketCoursesToFormatA(rsvp.responses?.TicketCourses);
            const selection = getSelectionFromFormatA(normalized, ticket.id, course.id);
            if (selection) {
              courseCounts.set(selection, (courseCounts.get(selection) || 0) + 1);
            } else {
              courseCounts.set('Not specified', (courseCounts.get('Not specified') || 0) + 1);
            }
          }
        });

        // Sort by count descending
        const sortedCounts = Array.from(courseCounts.entries()).sort((a, b) => b[1] - a[1]);

        if (sortedCounts.length > 0) {
          // Table with modern styling
          doc.setFillColor(248, 250, 252);
          doc.rect(10, yPos - 2, 190, 8, 'F');
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.5);
          doc.rect(10, yPos - 2, 190, 8);
          
          // Table header
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 58, 95);
          doc.text('Choice', 20, yPos + 2);
          doc.text('Quantity', 160, yPos + 2);
          yPos += 10;

          // Display counts with alternating row colors
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);

          sortedCounts.forEach(([selection, count], index) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            
            // Alternating row background
            if (index % 2 === 0) {
              doc.setFillColor(252, 252, 252);
              doc.rect(10, yPos - 2, 190, 6, 'F');
            }
            
            // Wrap long text if needed
            const maxWidth = 135;
            const lines = doc.splitTextToSize(selection, maxWidth);
            
            doc.text(lines, 20, yPos);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 58, 95);
            doc.text(count.toString(), 160, yPos);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 0);
            
            yPos += lines.length * 5 + 2;
          });

          yPos += 8;
        }
      });

      yPos += 10;
    });
  } else if (event.courses && event.courses.length > 0) {
    // Legacy global courses structure (backward compatibility)
    event.courses.forEach((course) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      // Course header with modern styling
      doc.setFillColor(30, 58, 95);
      doc.rect(10, yPos - 3, 190, 10, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(course.name.toUpperCase(), 15, yPos + 3);
      yPos += 12;

      // Count selections
      const courseCounts = new Map<string, number>();
      rsvps.forEach((rsvp) => {
        const selection = rsvp.responses?.[course.name] || 'Not specified';
        courseCounts.set(selection, (courseCounts.get(selection) || 0) + 1);
      });

      // Sort by count descending
      const sortedCounts = Array.from(courseCounts.entries()).sort((a, b) => b[1] - a[1]);

      // Table with modern styling
      doc.setFillColor(248, 250, 252);
      doc.rect(10, yPos - 2, 190, 8, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.rect(10, yPos - 2, 190, 8);
      
      // Table header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95);
      doc.text('Choice', 20, yPos + 2);
      doc.text('Quantity', 160, yPos + 2);
      yPos += 10;

      // Display counts with alternating row colors
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);

      sortedCounts.forEach(([selection, count], index) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        // Alternating row background
        if (index % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(10, yPos - 2, 190, 6, 'F');
        }
        
        // Wrap long text if needed
        const maxWidth = 135;
        const lines = doc.splitTextToSize(selection, maxWidth);
        
        doc.text(lines, 20, yPos);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 58, 95);
        doc.text(count.toString(), 160, yPos);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        
        yPos += lines.length * 5 + 2;
      });

      yPos += 10;
    });
  } else {
    // Legacy meal breakdown with modern styling
    const mealCounts = new Map<string, number>();
    rsvps.forEach((rsvp) => {
      const meal = rsvp.responses?.Meal || 'Not specified';
      mealCounts.set(meal, (mealCounts.get(meal) || 0) + 1);
    });

    const sortedMeals = Array.from(mealCounts.entries()).sort((a, b) => b[1] - a[1]);

    // Table with modern styling
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPos - 2, 190, 8, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.rect(10, yPos - 2, 190, 8);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    doc.text('Meal Choice', 20, yPos + 2);
    doc.text('Quantity', 160, yPos + 2);
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    sortedMeals.forEach(([meal, count], index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Alternating row background
      if (index % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(10, yPos - 2, 190, 6, 'F');
      }
      
      doc.text(meal, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95);
      doc.text(count.toString(), 160, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      yPos += 6;
    });
  }

  // ===== DIETARY REQUIREMENTS SUMMARY =====
  const allDietary = new Map<string, number>();
  rsvps.forEach((rsvp) => {
    if (rsvp.responses?.Dietary && Array.isArray(rsvp.responses.Dietary)) {
      rsvp.responses.Dietary.forEach((diet: string) => {
        allDietary.set(diet, (allDietary.get(diet) || 0) + 1);
      });
    }
  });

  if (allDietary.size > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }

    // Section header with modern styling
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPos - 5, 190, 12, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.rect(10, yPos - 5, 190, 12);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 58, 95);
    doc.text('DIETARY REQUIREMENTS', 15, yPos + 3);
    yPos += 15;

    // Dietary requirements with modern table styling
    doc.setFillColor(248, 250, 252);
    doc.rect(10, yPos - 2, 190, 8, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.rect(10, yPos - 2, 190, 8);
    
    // Table header
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 95);
    doc.text('Requirement', 20, yPos + 2);
    doc.text('Count', 160, yPos + 2);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    let dietaryIndex = 0;
    allDietary.forEach((count, diet) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Alternating row background
      if (dietaryIndex % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(10, yPos - 2, 190, 6, 'F');
      }
      
      doc.text(`• ${diet}`, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95);
      doc.text(count.toString(), 160, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      yPos += 6;
      dietaryIndex++;
    });
  }

  // ===== GUEST DETAILS TABLE =====
  doc.addPage();
  yPos = 20;

  // Section header with modern styling
  doc.setFillColor(248, 250, 252);
  doc.rect(10, yPos - 5, 190, 12, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.rect(10, yPos - 5, 190, 12);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text('GUEST DETAILS', 15, yPos + 3);
  yPos += 15;

  // Sort guests by room, then table, then seat
  const sortedRsvps = [...rsvps].sort((a, b) => {
    const aAssign = tableAssignments.get(a.id);
    const bAssign = tableAssignments.get(b.id);
    
    if (!aAssign && !bAssign) return 0;
    if (!aAssign) return 1;
    if (!bAssign) return -1;
    
    if (aAssign.roomName !== bAssign.roomName) {
      return aAssign.roomName.localeCompare(bAssign.roomName);
    }
    if (aAssign.tableName !== bAssign.tableName) {
      return aAssign.tableName.localeCompare(bAssign.tableName);
    }
    return aAssign.seatNumber - bAssign.seatNumber;
  });

  // Determine which courses to display based on event structure
  let coursesToDisplay: Array<{ ticketId?: string; ticketName?: string; course: any }> = [];
  
  if (event.tickets && event.tickets.length > 0) {
    // Collect all courses from all tickets (for header display)
    event.tickets.forEach((ticket) => {
      if (ticket.courses && ticket.courses.length > 0) {
        ticket.courses.forEach((course) => {
          coursesToDisplay.push({ ticketId: ticket.id, ticketName: ticket.name, course });
        });
      }
    });
  } else if (event.courses && event.courses.length > 0) {
    // Legacy global courses
    event.courses.forEach((course) => {
      coursesToDisplay.push({ course });
    });
  }

  // Table header with modern styling
  doc.setFillColor(30, 58, 95);
  doc.rect(10, yPos - 2, 190, 8, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  
  let colX = 12;
  doc.text('Name', colX, yPos + 2);
  colX += 40;
  
  coursesToDisplay.forEach((item) => {
    if (item.course && item.course.name) {
      const shortName = item.course.name.length > 10 ? item.course.name.substring(0, 10) : item.course.name;
      doc.text(shortName, colX, yPos + 2);
      colX += 35;
    }
  });
  
  doc.text('Room', colX, yPos + 2);
  colX += 25;
  doc.text('Table', colX, yPos + 2);
  colX += 20;
  doc.text('Seat', colX, yPos + 2);
  
  yPos += 10;

  // Guest rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);

  sortedRsvps.forEach((rsvp, index) => {
    if (yPos > 280) {
      doc.addPage();
      yPos = 20;
      
      // Repeat header on new page with modern styling
      doc.setFillColor(30, 58, 95);
      doc.rect(10, yPos - 2, 190, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      
      colX = 12;
      doc.text('Name', colX, yPos + 2);
      colX += 40;
      
      coursesToDisplay.forEach((item) => {
        if (item.course && item.course.name) {
          const shortName = item.course.name.length > 10 ? item.course.name.substring(0, 10) : item.course.name;
          doc.text(shortName, colX, yPos + 2);
          colX += 35;
        }
      });
      
      doc.text('Room', colX, yPos + 2);
      colX += 25;
      doc.text('Table', colX, yPos + 2);
      colX += 20;
      doc.text('Seat', colX, yPos + 2);
      
      yPos += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
    }

    // Alternate row background with better colors
    if (index % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(10, yPos - 3, 190, 5, 'F');
    }

    colX = 12;
    const guestName = `${rsvp.firstName || ''} ${rsvp.lastName || rsvp.name}`.trim();
    const truncatedName = guestName.length > 25 ? guestName.substring(0, 25) + '...' : guestName;
    doc.text(truncatedName, colX, yPos);
    colX += 40;

    // Course selections - handle both ticket-based and legacy structures
    coursesToDisplay.forEach((item) => {
      let selection = 'N/A';
      
      if (item.ticketId && item.course) {
        const rsvpTickets = rsvp.responses?.Tickets;
        if (Array.isArray(rsvpTickets) && rsvpTickets.some((t: any) => t && t.id === item.ticketId)) {
          const normalized = normalizeTicketCoursesToFormatA(rsvp.responses?.TicketCourses);
          const resolved = getSelectionFromFormatA(normalized, item.ticketId, item.course.id);
          if (resolved) selection = resolved;
        }
      } else if (item.course && item.course.name) {
        // Legacy: get from responses by course name
        selection = rsvp.responses?.[item.course.name] || 'N/A';
        if (typeof selection !== 'string') {
          selection = 'N/A';
        }
      }
      
      const truncated = selection.length > 20 ? selection.substring(0, 20) + '...' : selection;
      doc.text(truncated, colX, yPos);
      colX += 35;
    });

    // Location info
    const assignment = tableAssignments.get(rsvp.id);
    if (assignment) {
      doc.text(assignment.roomName.substring(0, 15), colX, yPos);
      colX += 25;
      doc.text(assignment.tableName.substring(0, 12), colX, yPos);
      colX += 20;
      doc.text(assignment.seatNumber.toString(), colX, yPos);
    } else {
      doc.setTextColor(150, 150, 150);
      doc.text('Not seated', colX, yPos);
      doc.setTextColor(0, 0, 0);
    }

    yPos += 5;
  });

  // ===== TABLE PLAN PAGES =====
  if (roomTablePlans && roomTablePlans.length > 0) {
    for (const room of roomTablePlans) {
      doc.addPage('l'); // Landscape for table plans
      yPos = 20;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(30, 58, 95);
      doc.text(`TABLE PLAN: ${room.name.toUpperCase()}`, 148, yPos, { align: 'center' });
      yPos += 15;

      // Create a simplified visual representation
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      let tableYPos = yPos;
      room.tables.forEach((table, index) => {
        if (tableYPos > 180) {
          tableYPos = 20;
        }

        const tableX = 20 + (index % 3) * 85;
        const tableY = tableYPos + Math.floor(index / 3) * 50;

        // Draw table box
        doc.setDrawColor(100, 100, 100);
        doc.setFillColor(240, 240, 240);
        doc.rect(tableX, tableY, 75, 45, 'FD');

        // Table name
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(30, 58, 95);
        doc.text(getTableNameFromTable(table), tableX + 37.5, tableY + 7, { align: 'center' });

        // Seated guests
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(50, 50, 50);
        
        const seatedGuests = table.seating.filter(s => s.rsvpId).length;
        doc.text(`${seatedGuests}/${table.seats} seats`, tableX + 37.5, tableY + 13, { align: 'center' });

        // List guests (up to 6)
        let guestY = tableY + 18;
        const seatedList = table.seating.filter(s => s.rsvpId).slice(0, 6);
        
        seatedList.forEach((seat) => {
          const guest = rsvps.find(r => r.id === seat.rsvpId);
          if (guest) {
            const guestName = `${guest.firstName || ''} ${guest.lastName || guest.name}`.trim();
            const shortName = guestName.length > 20 ? guestName.substring(0, 20) + '.' : guestName;
            doc.text(`${seat.seatNumber}. ${shortName}`, tableX + 3, guestY);
            guestY += 4;
          }
        });

        if (seatedGuests > 6) {
          doc.setTextColor(100, 100, 100);
          doc.text(`...and ${seatedGuests - 6} more`, tableX + 3, guestY);
        }

        if ((index + 1) % 3 === 0) {
          tableYPos += 50;
        }
      });
    }
  }

  // ===== FLOOR PLAN OVERVIEW PAGE =====
  if (roomTablePlans && roomTablePlans.length > 0) {
    doc.addPage('l'); // Landscape for floor plan
    yPos = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(30, 58, 95);
    doc.text('FLOOR PLAN OVERVIEW', 148, yPos, { align: 'center' });
    yPos += 20;

    // Create a simplified floor plan showing all rooms and tables
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    let currentX = 20;
    let currentY = yPos;
    const maxTablesPerRow = 4;
    const tableSpacing = 80;
    const rowSpacing = 60;

    roomTablePlans.forEach((room, roomIndex) => {
      // Room header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(30, 58, 95);
      doc.text(`${room.name.toUpperCase()}`, currentX, currentY);
      currentY += 15;

      // Draw tables for this room
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);

      room.tables.forEach((table, tableIndex) => {
        // Calculate position
        const tableX = currentX + (tableIndex % maxTablesPerRow) * tableSpacing;
        const tableY = currentY + Math.floor(tableIndex / maxTablesPerRow) * rowSpacing;

        // Draw table box
        doc.setDrawColor(100, 100, 100);
        doc.setFillColor(240, 240, 240);
        doc.rect(tableX, tableY, 70, 50, 'FD');

        // Table name
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(30, 58, 95);
        doc.text(getTableNameFromTable(table), tableX + 35, tableY + 8, { align: 'center' });

        // Seated guests count
        const seatedGuests = table.seating.filter(s => s.rsvpId).length;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(50, 50, 50);
        doc.text(`${seatedGuests}/${table.seats}`, tableX + 35, tableY + 18, { align: 'center' });

        // Show first few guests
        let guestY = tableY + 25;
        const seatedList = table.seating.filter(s => s.rsvpId).slice(0, 4);
        
        seatedList.forEach((seat) => {
          const guest = rsvps.find(r => r.id === seat.rsvpId);
          if (guest) {
            const guestName = `${guest.firstName || ''} ${guest.lastName || guest.name}`.trim();
            const shortName = guestName.length > 12 ? guestName.substring(0, 12) + '.' : guestName;
            doc.text(`${seat.seatNumber}. ${shortName}`, tableX + 3, guestY);
            guestY += 4;
          }
        });

        if (seatedGuests > 4) {
          doc.setTextColor(100, 100, 100);
          doc.text(`+${seatedGuests - 4} more`, tableX + 3, guestY);
        }
      });

      // Move to next room position
      const tablesInRoom = room.tables.length;
      const rowsInRoom = Math.ceil(tablesInRoom / maxTablesPerRow);
      currentY += (rowsInRoom * rowSpacing) + 30;

      // If we're running out of space, start a new column
      if (currentY > 180) {
        currentX += 320; // Move to next column
        currentY = yPos;
      }
    });

    // Add legend
    const legendY = 200;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(30, 58, 95);
    doc.text('LEGEND:', 20, legendY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text('• Table boxes show: Table Name, Seated/Total Seats, Guest List', 20, legendY + 8);
    doc.text('• Numbers indicate seat assignments', 20, legendY + 16);
    doc.text('• Guest names truncated for space', 20, legendY + 24);
  }

  doc.save(`${event.name} - Booking Form.pdf`);
}

interface BookingFormInfoRow {
  label: string;
  value: string | string[];
  large?: boolean;
}

interface BookingFormMealOption {
  option: string;
  count: number;
}

interface BookingFormMealCourse {
  name: string;
  options: BookingFormMealOption[];
}

interface BookingFormMealTicketGroup {
  title: string;
  courses: BookingFormMealCourse[];
}

interface BookingFormMealSelectionData {
  ticketGroups: BookingFormMealTicketGroup[];
  courseGroups: BookingFormMealCourse[];
  fallback: Array<{ name: string; count: number }>;
}

export interface BookingFormPdfData {
  eventName: string;
  eventId: string;
  generatedDate: string;
  unitDisplayName: string;
  infoRowsBeforeNumbers: BookingFormInfoRow[];
  infoRowsAfterNumbers: BookingFormInfoRow[];
  numbersDining: number;
  mealSelection: BookingFormMealSelectionData;
  dietaryRequirements: string[];
  rsvpDetails: Array<{
    guestName: string;
    roomName: string;
    tableName: string;
    seatNumber: string;
    ticketSummary: string[];
    courseSelections: Record<string, string[]>;
    allergies: string;
  }>;
  courseColumns: string[];
}

export async function generateBookingFormPdf(data: BookingFormPdfData): Promise<void> {
  const doc = new jsPDF('p', 'mm', 'a4');
  const marginX = 15;
  const contentWidth = 180;
  const labelWidth = 68;
  const valueWidth = contentWidth - labelWidth;
  const lineHeight = 5.5;

  const blue = { r: 31, g: 58, b: 117 };
  const slate = { r: 51, g: 65, b: 85 };
  const border = { r: 148, g: 163, b: 184 };

  const renderHeader = (showInstructions: boolean): number => {
    doc.setFillColor(blue.r, blue.g, blue.b);
    doc.rect(0, 0, 210, 32, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(data.unitDisplayName || data.eventName, marginX, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Generated on ${data.generatedDate}`, 210 - marginX, 14, { align: 'right' });
    doc.text(`Event ID: ${data.eventId}`, 210 - marginX, 20, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('FINAL BOOKING FORM', 105, 26, { align: 'center' });

    doc.setTextColor(slate.r, slate.g, slate.b);
    doc.setFont('helvetica', 'normal');
    return 45;
  };

  let currentY = renderHeader(true);

  const startNewPage = () => {
    doc.addPage();
    currentY = 20;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(slate.r, slate.g, slate.b);
  };

  const ensureSpace = (neededHeight: number) => {
    if (currentY + neededHeight > 280) {
      startNewPage();
    }
  };

  // Submission schedule table
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  // Label/value rows
  const renderLabelValueRow = (row: BookingFormInfoRow) => {
    const valueArray = Array.isArray(row.value) ? row.value : [row.value];
    const wrappedLines = valueArray.flatMap((entry) =>
      doc.splitTextToSize(entry || 'Not specified', valueWidth - 6)
    );
    const rowLineHeight = lineHeight;
    const rowHeight = Math.max(wrappedLines.length, 1) * rowLineHeight + 4;

    ensureSpace(rowHeight);

    doc.setFillColor(blue.r, blue.g, blue.b);
    doc.setTextColor(255, 255, 255);
    doc.rect(marginX, currentY, labelWidth, rowHeight, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(row.label, marginX + 3, currentY + 4.5);

    doc.setDrawColor(border.r, border.g, border.b);
    doc.rect(marginX + labelWidth, currentY, valueWidth, rowHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(slate.r, slate.g, slate.b);

    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    doc.line(marginX + labelWidth, currentY, marginX + labelWidth, currentY + rowHeight);
    doc.setLineWidth(0.2);
    doc.setDrawColor(border.r, border.g, border.b);

    wrappedLines.forEach((line, index) => {
      doc.text(line, marginX + labelWidth + 3, currentY + 4.5 + (index * rowLineHeight));
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    currentY += rowHeight;
  };

  const combinedRows: BookingFormInfoRow[] = [
    ...data.infoRowsBeforeNumbers,
    {
      label: 'Numbers dining',
      value: String(data.numbersDining),
    },
    ...data.infoRowsAfterNumbers,
  ];

  combinedRows.forEach(renderLabelValueRow);

  // RSVP detail table
  if (data.rsvpDetails.length > 0) {
    const guestsByRoom = data.rsvpDetails.reduce<Record<string, BookingFormPdfData['rsvpDetails']>>((accumulator, detail) => {
      const key = detail.roomName || 'Unassigned';
      if (!accumulator[key]) {
        accumulator[key] = [];
      }
      accumulator[key].push(detail);
      return accumulator;
    }, {});

    const roomNamesSorted = Object.keys(guestsByRoom).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
    );

    let firstRoom = true;

    roomNamesSorted.forEach((roomName) => {
      const guests = guestsByRoom[roomName];
      const guestsSortedBySeat = sortBookingFormGuestsBySeatAsc(guests);

      if (firstRoom) {
        doc.addPage('landscape');
        firstRoom = false;
      } else {
        doc.addPage('landscape');
      }

      let tableY = 20;
      const tableMarginX = 15;
      const tableWidth = doc.internal.pageSize.getWidth() - tableMarginX * 2;
      const tableHeightLimit = doc.internal.pageSize.getHeight() - 20;

      const ensureTableSpace = (height: number) => {
        if (tableY + height > tableHeightLimit) {
          doc.addPage('landscape');
          tableY = 20;
        }
      };

      const tableTitleFontSize = 9;
      const tableHeaderFontSize = 7;
      const tableCellFontSize = 6;
      const tableHeaderHeight = 6;
      const tableRowBaseHeight = 5;
      const tableCellLineHeight = 3.2;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(tableTitleFontSize);
      doc.setTextColor(blue.r, blue.g, blue.b);
      doc.text(`${roomName} – Guest Seating & Meal Selections`, tableMarginX, tableY);
      tableY += 5;

      const guestColumnWidth = 32;
      const seatColumnWidth = 28;
      const ticketColumnWidth = 26;
      const allergyColumnWidth = 20;
      const remainingWidth = tableWidth - guestColumnWidth - seatColumnWidth - ticketColumnWidth - allergyColumnWidth;
      const courseColumnCount = Math.max(data.courseColumns.length, 1);
      const courseColumnWidth = Math.max(20, remainingWidth / courseColumnCount);

      const baseColumns = [
        {
          header: 'Guest',
          width: guestColumnWidth,
          getLines: (detail: BookingFormPdfData['rsvpDetails'][number]) => [detail.guestName || '—'],
        },
        {
          header: 'Seat',
          width: seatColumnWidth,
          getLines: (detail: BookingFormPdfData['rsvpDetails'][number]) => [
            `${detail.tableName || 'Table'} / ${detail.seatNumber || '—'}`,
          ],
        },
        {
          header: 'Tickets',
          width: ticketColumnWidth,
          getLines: (detail: BookingFormPdfData['rsvpDetails'][number]) =>
            detail.ticketSummary.length > 0 ? detail.ticketSummary : ['—'],
        },
      ];

      const courseColumns = (data.courseColumns.length > 0 ? data.courseColumns : ['Courses']).map((courseName) => ({
        header: courseName,
        width: courseColumnWidth,
        getLines: (detail: BookingFormPdfData['rsvpDetails'][number]) =>
          detail.courseSelections[courseName] && detail.courseSelections[courseName].length > 0
            ? detail.courseSelections[courseName]
            : ['No selection'],
      }));

      const allergyColumn = {
        header: 'Allergies',
        width: allergyColumnWidth,
        getLines: (detail: BookingFormPdfData['rsvpDetails'][number]) =>
          detail.allergies ? detail.allergies.split(/\r?\n/) : ['—'],
      };

      const columns = [...baseColumns, ...courseColumns, allergyColumn];

      ensureTableSpace(tableHeaderHeight + 2);
      doc.setFillColor(blue.r, blue.g, blue.b);
      doc.rect(tableMarginX, tableY, tableWidth, tableHeaderHeight, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(tableHeaderFontSize);

      let headerX = tableMarginX;
      columns.forEach((column) => {
        doc.text(column.header, headerX + 2, tableY + tableHeaderHeight - 1.6);
        headerX += column.width;
      });
      tableY += tableHeaderHeight;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(tableCellFontSize);
      doc.setTextColor(slate.r, slate.g, slate.b);

      guestsSortedBySeat.forEach((detail, rowIndex) => {
        const columnLines = columns.map((column) => {
          const lines = column.getLines(detail);
          return lines.flatMap((line) => doc.splitTextToSize(line, column.width - 4));
        });

        const rowHeight = Math.max(
          tableRowBaseHeight,
          ...columnLines.map((lines) => lines.length * tableCellLineHeight + 1.2)
        );

        ensureTableSpace(rowHeight + 3);

        if (rowIndex % 2 === 0) {
          doc.setFillColor(245, 247, 250);
          doc.rect(tableMarginX, tableY, tableWidth, rowHeight, 'F');
        }

        doc.setDrawColor(border.r, border.g, border.b);
        doc.rect(tableMarginX, tableY, tableWidth, rowHeight);

        let cellX = tableMarginX + 2;
        columns.forEach((column, columnIndex) => {
          const lines = columnLines[columnIndex];
          let cellY = tableY + 3.2;
          lines.forEach((line, lineIndex) => {
            doc.text(line, cellX, cellY);
            if (lineIndex < lines.length - 1) {
              cellY += tableCellLineHeight;
            }
          });
          cellX += column.width;
        });

        tableY += rowHeight;
      });
    });
  }

  doc.save(`${data.eventName} - Booking Form.pdf`);
}

/**
 * Generate Attendance List PDF
 * Simple list of all attendees with meal choices
 */
export async function generateAttendanceList(
  rsvps: RSVP[],
  event: Event,
  tableAssignments?: Record<string, { tableName: string; seatNumber: number }>,
  invoices: Record<string, Invoice> = {},
  allRsvps?: RSVP[],
): Promise<void> {
  if (!rsvps || rsvps.length === 0) {
    throw new Error('No RSVPs provided for attendance list generation');
  }
  
  if (!event) {
    throw new Error('No event provided for attendance list generation');
  }

  const rsvpList = allRsvps ?? rsvps;
  const multiTenant = isMultiTenantEvent(event);

  const doc = new jsPDF();
  let yPos = 20;

  const normalizeTicketEntries = (value: any): Array<any> => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'object') {
      return Object.values(value);
    }
    return [];
  };

  /** Sum of `responses.Tickets[].qty` for one RSVP row; multi-tenant bookings can hold several ticket lines. */
  const getRowTicketQuantity = (rsvp: RSVP): number => {
    const ticketEntries = normalizeTicketEntries(rsvp.responses?.Tickets).filter((t) => t);
    if (ticketEntries.length === 0) {
      return 0;
    }
    return ticketEntries.reduce((sum, t) => sum + (Math.max(0, Number(t?.qty) || 0) || 1), 0);
  };

  /** Sum of qty x current ticket price for one RSVP row (mirrors the Total/Owed amounts logic). */
  const getRowTicketAmount = (rsvp: RSVP): number => {
    const ticketEntries = normalizeTicketEntries(rsvp.responses?.Tickets).filter((t) => t);
    return ticketEntries.reduce((sum, t) => {
      const ticketType = event.tickets?.find((tt) => tt.id === t.id);
      const price = Number(ticketType?.price ?? t.price ?? 0) || 0;
      const qty = Math.max(0, Number(t?.qty) || 0) || 1;
      return sum + price * qty;
    }, 0);
  };

  const buildMealSummary = (rsvp: RSVP): string => {
    if (event.tickets && event.tickets.length > 0) {
      const ticketEntries = normalizeTicketEntries(rsvp.responses?.Tickets).filter((t) => t);
      if (ticketEntries.length === 0) {
        return 'No meal selected';
      }

      if (multiTenant) {
        const ticketSummaries = ticketEntries.map((ticket) => {
          const ticketType = event.tickets?.find((tt) => tt.id === ticket.id);
          const ticketLabel = ticket.name || ticketType?.name || 'Ticket';
          const qty = Math.max(0, Number(ticket?.qty) || 0) || 1;
          const price = Number(ticketType?.price ?? ticket.price ?? 0) || 0;
          return `${ticketLabel} x${qty} (${formatCurrency(price * qty)})`;
        });
        return ticketSummaries.join(' | ');
      }

      const normalizedTc = normalizeTicketCoursesToFormatA(
        (rsvp.responses as any)?.TicketCourses
      );
      const ticketSummaries = ticketEntries.map((ticket) => {
        const ticketType = event.tickets?.find((tt) => tt.id === ticket.id);
        const courseSelections: Array<{ courseName: string; mealChoice: string }> = [];

        if (ticketType?.courses) {
          ticketType.courses.forEach((course) => {
            const selection = getSelectionFromFormatA(
              normalizedTc,
              ticket.id,
              course.id
            );
            if (selection) {
              courseSelections.push({
                courseName: course.name,
                mealChoice: selection,
              });
            }
          });
        }

        if (courseSelections.length === 0) {
          return ticket.name || ticketType?.name || 'Ticket';
        }

        const courseText = courseSelections.map((cs) => `${cs.courseName}: ${cs.mealChoice}`).join('; ');
        const ticketLabel = ticket.name || ticketType?.name || 'Ticket';
        return `${ticketLabel} (${courseText})`;
      });

      return ticketSummaries.join(' | ');
    }

    if (event.courses && event.courses.length > 0) {
      const courseSummaries = event.courses
        .map((course) => {
          const selection = rsvp.responses?.[course.name];
          if (selection) {
            return `${course.name}: ${selection}`;
          }
          return null;
        })
        .filter(Boolean) as string[];

      return courseSummaries.length > 0 ? courseSummaries.join(' | ') : 'No meal selected';
    }

    if (rsvp.responses?.Meal) {
      return rsvp.responses.Meal;
    }

    return 'No meal selected';
  };

  // Title
  doc.setFontSize(18);
  doc.setTextColor(30, 58, 95);
  doc.text('ATTENDANCE LIST', 105, yPos, { align: 'center' });
  yPos += 10;

  // Event details
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(event.name, 105, yPos, { align: 'center' });
  yPos += 6;
  doc.setFontSize(10);
  doc.text(
    event.date.toLocaleDateString('en-GB'),
    105,
    yPos,
    { align: 'center' }
  );
  yPos += 15;

  // Table headers
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Name', 15, yPos);
  doc.text('Rank', 70, yPos);
  doc.text('Table / Seat', 110, yPos);
  doc.text(multiTenant ? 'Tickets' : 'Meal', 140, yPos);
  doc.text('Payment', 170, yPos);
  doc.text('Owed', 200, yPos, { align: 'right' });
  yPos += 2;

  // Header line
  doc.setLineWidth(0.5);
  doc.line(10, yPos, 200, yPos);
  yPos += 5;

  // Guest list
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  rsvps.forEach((rsvp, index) => {
    const questionAnswers = formatRsvpQuestionAnswers(rsvp, event);
    const questionAnswerLines: string[] = questionAnswers ? doc.splitTextToSize(questionAnswers, 185) : [];

    if (yPos + questionAnswerLines.length * 4 > 280) {
      doc.addPage();
      yPos = 20;
    }

    doc.setTextColor(0, 0, 0);
    const seating = tableAssignments?.[rsvp.id];
    const tableDisplay = seating
      ? `${seating.tableName} / Seat ${seating.seatNumber}`
      : 'Not assigned';

    const mealDisplay = buildMealSummary(rsvp);
    const paymentDisplay = getAttendanceExportPaymentStatusLabel(rsvp, event);
    const amounts = getAttendanceExportAmounts(rsvp, rsvpList, event, invoices);

    doc.setTextColor(0, 0, 0);
    doc.text(`${index + 1}. ${rsvp.name}`, 15, yPos);
    doc.text(rsvp.rank || '', 70, yPos);
    doc.text(tableDisplay, 110, yPos);

    // Wrap ticket/meal text within its column so multi-ticket bookings (e.g. multi-tenant
    // "Adult x2 | Child x1") don't overrun into the Payment/Owed columns.
    const mealLines = doc.splitTextToSize(mealDisplay, 28);
    doc.text(mealLines, 140, yPos);
    const mealLineCount = Array.isArray(mealLines) ? mealLines.length : 1;

    if (paymentDisplay === 'Apology') {
      doc.setTextColor(217, 119, 6); // Amber
    } else if (paymentDisplay === 'Host Pays') {
      doc.setTextColor(100, 100, 100); // Neutral
    } else if (paymentDisplay === 'Paid') {
      doc.setTextColor(16, 185, 129); // Green
    } else {
      doc.setTextColor(239, 68, 68); // Red
    }

    doc.text(paymentDisplay, 170, yPos);
    doc.setTextColor(0, 0, 0);
    doc.text(amounts ? formatCurrency(amounts.outstanding) : '', 200, yPos, { align: 'right' });

    doc.setTextColor(100, 100, 100);
    const extraLine = rsvp.unit ? `Unit: ${rsvp.unit}` : '';
    // Extra row height needed for wrapped ticket lines beyond the first
    let rowBottomOffset = (mealLineCount - 1) * 4;
    if (extraLine) {
      const unitOffset = Math.max(4, rowBottomOffset + 4);
      doc.text(extraLine, 15, yPos + unitOffset);
      rowBottomOffset = Math.max(rowBottomOffset, unitOffset);
    }
    if (questionAnswerLines.length > 0) {
      doc.text(questionAnswerLines, 15, yPos + rowBottomOffset + 4);
      rowBottomOffset += questionAnswerLines.length * 4;
    }

    doc.setTextColor(0, 0, 0);
    yPos += 6 + rowBottomOffset;
  });

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  const paidCount = rsvps.filter((r) => getAttendanceExportPaymentStatusLabel(r, event) === 'Paid').length;
  const hostPaysCount = rsvps.filter((r) => getAttendanceExportPaymentStatusLabel(r, event) === 'Host Pays').length;
  const outstandingCount = rsvps.filter((r) => getAttendanceExportPaymentStatusLabel(r, event) === 'Unpaid').length;

  if (multiTenant) {
    yPos = 280;
    const totalTicketQty = rsvps.reduce((sum, r) => sum + getRowTicketQuantity(r), 0);
    const totalTicketAmount = rsvps.reduce((sum, r) => sum + getRowTicketAmount(r), 0);
    doc.text(
      `Total Tickets Sold: ${totalTicketQty} | Total Ticket Revenue: ${formatCurrency(totalTicketAmount)}`,
      105,
      yPos,
      { align: 'center' }
    );
  }

  yPos = 285;
  doc.text(
    `Total Attendees: ${rsvps.length} | Paid: ${paidCount} | Host pays: ${hostPaysCount} | Outstanding: ${outstandingCount}`,
    105,
    yPos,
    { align: 'center' }
  );

  doc.save(`${event.name} - Attendance List.pdf`);
}

/**
 * Generate Table Cards PDF
 * Creates cards showing who sits at each table
 */
export async function generateTableCards(
  tables: SeatingTable[],
  rsvps: RSVP[],
  event: Event
): Promise<void> {
  const doc = new jsPDF();
  const rsvpMap = new Map(rsvps.map((r) => [r.id, r]));

  tables.forEach((table, tableIndex) => {
    if (tableIndex > 0) {
      doc.addPage();
    }

    let yPos = 20;

    // Event name
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(event.name, 105, yPos, { align: 'center' });
    yPos += 10;

    // Table name (large)
    doc.setFontSize(24);
    doc.setTextColor(30, 58, 95);
    doc.text(getTableNameFromTable(table), 105, yPos, { align: 'center' });
    yPos += 10;

    // Table type
    doc.setFontSize(12);
    doc.setTextColor(212, 175, 55); // Gold
    doc.text(`${table.type} table • ${table.seats} seats`, 105, yPos, { align: 'center' });
    yPos += 15;

    // Guest list
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    table.seating.forEach((seat) => {
      if (seat.rsvpId) {
        const guest = rsvpMap.get(seat.rsvpId);
        if (guest) {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFont('helvetica', 'bold');
          doc.text(`Seat ${seat.seatNumber}:`, 20, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(`${guest.name} (${guest.rank})`, 45, yPos);
          
          // Meal/Course choice
          let mealInfo = '';
          if (event.courses && event.courses.length > 0) {
            const mainCourse = event.courses.find((c) => 
              c.name.toLowerCase().includes('main')
            ) || event.courses[0];
            if (guest.responses?.[mainCourse.name]) {
              mealInfo = guest.responses[mainCourse.name];
            }
          } else if (guest.responses?.Meal) {
            mealInfo = guest.responses.Meal;
          }
          
          if (mealInfo) {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text(`→ ${mealInfo}`, 45, yPos + 5);
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            yPos += 5;
          }

          yPos += 8;
        }
      }
    });
  });

  doc.save(`${event.name} - Table Cards.pdf`);
}

/**
 * Export CSV for spreadsheet software
 */
export function exportToCSV(
  rsvps: RSVP[], 
  event: Event, 
  tableAssignments?: Record<string, { tableName: string; seatNumber: number }>,
  unitProvince: string = '',
  invoices: Record<string, Invoice> = {},
  allRsvps?: RSVP[],
): void {
  const rsvpList = allRsvps ?? rsvps;
  const multiTenant = isMultiTenantEvent(event);
  const normalizeTicketEntries = (value: any): Array<any> => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'object') {
      return Object.values(value);
    }
    return [];
  };

  // Build headers to match the table display
  const headers = ['Name', 'Prefix', 'Rank', 'Email', 'Unit', 'Province', 'Guest Relationships', 'Table', 'Seat Number'];

  // Meal option columns (dynamic)
  const mealColumns: string[] = [];
  const includeTicketColumn = !!(event.tickets && event.tickets.length > 0);

  if (event.tickets && event.tickets.length > 0) {
    // Gather unique course names across all tickets
    const courseNameSet = new Set<string>();
    event.tickets.forEach((ticket) => {
      (ticket.courses || []).forEach((course) => {
        if (course?.name) {
          courseNameSet.add(course.name);
        }
      });
    });

    mealColumns.push(...Array.from(courseNameSet));

    // If tickets exist but no courses, fall back to a single Meal column
    if (mealColumns.length === 0) {
      mealColumns.push('Meal');
    }

    // Insert Tickets column (with quantity + subtotal for multi-tenant bookings) before meal choices
    headers.push('Tickets', 'Ticket Quantity', 'Ticket Amount', ...mealColumns);
  } else if (event.courses && event.courses.length > 0) {
    event.courses.forEach((course) => {
      mealColumns.push(course.name);
    });
    headers.push(...mealColumns);
  } else {
    mealColumns.push('Meal');
    headers.push('Meal');
  }
  
  // One column per additional question (dynamic)
  const questionColumns = (event.questionnaire ?? []).map((item) => item.question);
  headers.push(...questionColumns);

  headers.push('Special Requests', 'Dietary Requirements', 'Allergies', 'Payment Status', 'Total Amount', 'Amount Owed', 'Apologies', 'Checked In');
  
  const rows = rsvps.map((rsvp) => {
    // Calculate guest relationships
    const invitedByValue = typeof rsvp.invitedBy === 'string' && rsvp.invitedBy.trim().length > 0
      ? rsvp.invitedBy.trim()
      : undefined;

    const invitedByRsvp = invitedByValue
      ? rsvps.find((candidate) => {
          const candidateUserId = typeof candidate.userId === 'string' ? candidate.userId.trim() : '';
          return candidateUserId === invitedByValue || candidate.id === invitedByValue;
        })
      : undefined;

    const hostIdentifiers = [
      typeof rsvp.userId === 'string' && rsvp.userId.trim().length > 0 ? rsvp.userId.trim() : undefined,
      rsvp.id,
    ].filter((identifier): identifier is string => typeof identifier === 'string' && identifier.length > 0);

    const invitedGuests = rsvps.filter((guest) => {
      const guestInvitedBy =
        typeof guest.invitedBy === 'string' && guest.invitedBy.trim().length > 0
          ? guest.invitedBy.trim()
          : undefined;
      if (!guestInvitedBy) {
        return false;
      }
      return hostIdentifiers.includes(guestInvitedBy);
    });

    // Format guest relationships
    let guestRelationships = '';
    if (invitedByRsvp) {
      guestRelationships = `Guest of: ${invitedByRsvp.name}`;
    }
    if (invitedGuests.length > 0) {
      const guestNames = invitedGuests.map(g => g.name).join(', ');
      if (guestRelationships) {
        guestRelationships += ` | Hosts ${invitedGuests.length} guest(s): ${guestNames}`;
      } else {
        guestRelationships = `Hosts ${invitedGuests.length} guest(s): ${guestNames}`;
      }
    }
    if (!guestRelationships) {
      guestRelationships = 'No relationships';
    }

    // Get table assignment
    const tableInfo =
      tableAssignments?.[rsvp.id] ??
      (rsvp.userId && typeof rsvp.userId === 'string' ? tableAssignments?.[rsvp.userId] : undefined);
    const tableName = tableInfo ? tableInfo.tableName : '';
    const seatNumber = tableInfo ? tableInfo.seatNumber.toString() : '';

    // Format meal choices per column
    const mealColumnValues: Record<string, string[]> = {};
    mealColumns.forEach((col) => {
      mealColumnValues[col] = [];
    });

    let ticketNames: string[] = [];
    let ticketTotalQty = 0;
    let ticketTotalAmount = 0;

    if (event.tickets && event.tickets.length > 0) {
      const ticketEntries = normalizeTicketEntries(rsvp.responses?.Tickets).filter((t) => t);

      const normalizedTc = normalizeTicketCoursesToFormatA(
        (rsvp.responses as any)?.TicketCourses
      );
      ticketEntries.forEach((ticket) => {
        const ticketType = event.tickets?.find((tt) => tt.id === ticket.id);
        const ticketLabel = ticket.name || ticketType?.name || 'Ticket';
        const qty = Math.max(0, Number(ticket?.qty) || 0) || 1;
        const price = Number(ticketType?.price ?? ticket.price ?? 0) || 0;

        ticketTotalQty += qty;
        ticketTotalAmount += price * qty;
        ticketNames.push(multiTenant ? `${ticketLabel} x${qty}` : ticketLabel);

        if (ticketType?.courses && ticketType.courses.length > 0) {
          ticketType.courses.forEach((course) => {
            const selection = getSelectionFromFormatA(
              normalizedTc,
              ticket.id,
              course.id
            );
            if (selection && mealColumnValues[course.name]) {
              mealColumnValues[course.name].push(selection);
            }
          });
        } else if (mealColumnValues['Meal']) {
          // No courses defined; fall back to generic meal column with ticket name
          mealColumnValues['Meal'].push(ticketLabel);
        }
      });
    } else if (event.courses && event.courses.length > 0) {
      event.courses.forEach((course) => {
        const selection = rsvp.responses?.[course.name];
        if (selection && mealColumnValues[course.name]) {
          mealColumnValues[course.name].push(selection);
        }
      });
    } else if (rsvp.responses?.Meal && mealColumnValues['Meal']) {
      mealColumnValues['Meal'].push(rsvp.responses.Meal);
    }

    const mealColumnTexts = mealColumns.map((col) => (mealColumnValues[col] || []).join(' | '));
    const ticketNamesText = includeTicketColumn ? ticketNames.join(' | ') : undefined;

    // Build the row
    const row = [
      rsvp.name || '',
      rsvp.rank || '',
      rsvp.additionalRank || '',
      rsvp.email || '',
      rsvp.unit || '',
      unitProvince,
      guestRelationships,
      tableName,
      seatNumber,
    ];
    
    if (includeTicketColumn) {
      row.push(ticketNamesText || '', ticketTotalQty.toString(), ticketTotalAmount.toFixed(2));
    }

    // Add meal choices per column in the defined order
    row.push(...mealColumnTexts);

    const answersByQuestion = new Map(
      getRsvpQuestionAnswers(rsvp, event).map(({ question, answer }) => [question, answer]),
    );
    row.push(...questionColumns.map((question) => answersByQuestion.get(question) ?? ''));

    const paymentStatusDisplay = getAttendanceExportPaymentStatusLabel(rsvp, event);
    const amounts = getAttendanceExportAmounts(rsvp, rsvpList, event, invoices);
    row.push(
      rsvp.specialRequests || '',
      Array.isArray(rsvp.responses?.Dietary) ? rsvp.responses.Dietary.join('; ') : '',
      rsvp.responses?.Allergies || '',
      paymentStatusDisplay,
      amounts ? amounts.total.toFixed(2) : '',
      amounts ? amounts.outstanding.toFixed(2) : '',
      rsvp.apologies ? 'Yes' : 'No',
      rsvp.checkedIn ? 'Yes' : 'No'
    );
    
    return row;
  });

  const csvLine = (cells: string[]) => cells.map((cell) => {
    // Escape quotes and wrap in quotes
    const cellStr = String(cell || '').replace(/"/g, '""');
    return `"${cellStr}"`;
  }).join(',');

  const csvContent = [csvLine(headers), ...rows.map(csvLine)].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${event.name} - Guest List.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Generate Custom Place Cards PDF using template
 * Creates printable place cards based on user-designed template
 * Uses html2canvas for accurate rendering
 */
const LEGACY_FIELD_PLACEHOLDER: Partial<Record<PlaceCardField['type'], string>> = {
  eventName: '{event_name}',
  eventDate: '{event_date}',
  eventTitle: '{event_name}',
  fullName: '{guest_full_name}',
  firstName: '{guest_first_name}',
  lastName: '{guest_last_name}',
  // Legacy mapping: "rank" now represents the guest's rank (not prefix)
  rank: '{guest_rank}',
  rankAndName: '{guest_rank} {guest_full_name}',
  mealChoice: '{guest_meal}',
  tableName: '{guest_table}',
  seatNumber: '{guest_seat}',
  unitName: '{unit_name}',
  unitNumber: '{unit_number}',
};

const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

function convertLegacyFieldsToElements(fields?: PlaceCardField[]): PlaceCardElement[] {
  if (!fields || fields.length === 0) {
    return [];
  }

  return fields.map((field, index) => ({
    id: field.id ?? `legacy-field-${index}`,
    type: field.type === 'unitLogo' ? 'image' : 'text',
    name: field.label,
    x: (field.x ?? 0) / 100,
    y: (field.y ?? 0) / 100,
    width: (field.width ?? 20) / 100,
    height: (field.height ?? 10) / 100,
    content:
      field.type === 'customText'
        ? field.customValue ?? ''
        : LEGACY_FIELD_PLACEHOLDER[field.type] ?? field.label,
    fontSize: field.fontSize,
    fontWeight: (field.fontWeight as PlaceCardElement['fontWeight']) ?? 'normal',
    textAlign: field.textAlign,
    color: field.color,
    mirrored: field.flipped ?? false,
    mirrorRotation: 180,
    imageType: field.type === 'unitLogo' ? 'unit-logo' : undefined,
  }));
}

const FRAME_CSS_BORDER_STYLE: Partial<Record<PlaceCardFrame['style'], string>> = {
  solid: 'solid',
  dashed: 'dashed',
  dotted: 'dotted',
};

type FrameCssRect = { x: number; y: number; width: number; height: number };

function makeFrameStrip(x: number, y: number, width: number, height: number): HTMLDivElement {
  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.left = `${x}px`;
  div.style.top = `${y}px`;
  div.style.width = `${width}px`;
  div.style.height = `${height}px`;
  div.style.pointerEvents = 'none';
  div.style.boxSizing = 'border-box';
  return div;
}

/**
 * Sets a CSS border on a frame div. captureElementToPng's html2canvas onclone hook force-resets
 * `border-style: none !important` on every element except ones flagged `[data-keep-border]` (to
 * work around Tailwind Preflight's global border-color rule showing hairlines) — frame divs must
 * opt back in here or their borders are silently stripped from the exported PDF.
 */
function setFrameBorder(div: HTMLDivElement, borderCss: string): void {
  div.style.border = borderCss;
  div.dataset.keepBorder = 'true';
}

/**
 * Renders the "royal" wavy border + spiral corner scrolls as an SVG data URI, using the same
 * point math as the designer's Konva rendering so PDF output matches the on-screen preview.
 */
function buildRoyalFrameSvgDataUri(width: number, height: number, frame: PlaceCardFrame): string {
  const amplitude = frame.thickness * 1.6;
  const targetWavelength = Math.max(frame.thickness * 8, 24);
  const wavePath = smoothSvgPathFromPoints(buildWavyRectPoints(width, height, amplitude, targetWavelength), true);

  const curlSize = Math.min(width, height) * 0.22;
  const spiralPath = smoothSvgPathFromPoints(buildSpiralPoints(1.75, curlSize * 0.5, 40), false);
  const spiralStrokeWidth = Math.max(1, frame.thickness * 0.6);
  const corners = [
    { x: 0, y: 0, rotate: 0 },
    { x: width, y: 0, rotate: 90 },
    { x: width, y: height, rotate: 180 },
    { x: 0, y: height, rotate: 270 },
  ];
  const curlMarkup = corners
    .map(
      (c) =>
        `<g transform="translate(${c.x},${c.y}) rotate(${c.rotate})"><path d="${spiralPath}" fill="none" stroke="${frame.color}" stroke-width="${spiralStrokeWidth}" stroke-linecap="round" /></g>`
    )
    .join('');

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
    `<path d="${wavePath}" fill="none" stroke="${frame.color}" stroke-width="${frame.thickness}" stroke-linejoin="round" stroke-linecap="round" />` +
    `${curlMarkup}</svg>`;

  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

/** Builds the DOM nodes for one panel's frame, matching the style-specific Konva rendering used in the designer. */
function buildFrameNodesForRect(rect: FrameCssRect, frame: PlaceCardFrame): HTMLDivElement[] {
  const { x, y, width, height } = rect;

  if (frame.style === 'royal') {
    const div = makeFrameStrip(x, y, width, height);
    div.style.backgroundImage = `url(${buildRoyalFrameSvgDataUri(width, height, frame)})`;
    div.style.backgroundSize = '100% 100%';
    div.style.backgroundRepeat = 'no-repeat';
    return [div];
  }

  if (frame.style === 'double') {
    // CSS renders 'double' as a hairline unless the border is at least ~3px wide.
    const borderWidth = Math.max(frame.thickness, 3);
    const gap = frame.thickness * 2;
    const outer = makeFrameStrip(x, y, width, height);
    setFrameBorder(outer, `${borderWidth}px solid ${frame.color}`);
    const nodes = [outer];
    const innerX = x + frame.thickness + gap;
    const innerY = y + frame.thickness + gap;
    const innerWidth = width - (frame.thickness + gap) * 2;
    const innerHeight = height - (frame.thickness + gap) * 2;
    if (innerWidth > 0 && innerHeight > 0) {
      const inner = makeFrameStrip(innerX, innerY, innerWidth, innerHeight);
      setFrameBorder(inner, `${borderWidth}px solid ${frame.color}`);
      nodes.push(inner);
    }
    return nodes;
  }

  if (frame.style === 'modern') {
    const gap = Math.min(frame.thickness * 3, width / 4, height / 4);
    const top = makeFrameStrip(x + gap, y, width - gap * 2, frame.thickness);
    const bottom = makeFrameStrip(x + gap, y + height - frame.thickness, width - gap * 2, frame.thickness);
    const left = makeFrameStrip(x, y + gap, frame.thickness, height - gap * 2);
    const right = makeFrameStrip(x + width - frame.thickness, y + gap, frame.thickness, height - gap * 2);
    [top, bottom, left, right].forEach((div) => {
      div.style.backgroundColor = frame.color;
    });
    return [top, bottom, left, right];
  }

  if (frame.style === 'elegant') {
    const outerStroke = Math.max(1, frame.thickness * 0.6);
    const gap = frame.thickness * 2.2;
    const outer = makeFrameStrip(x, y, width, height);
    setFrameBorder(outer, `${outerStroke}px solid ${frame.color}`);
    const nodes = [outer];
    const innerWidth = width - gap * 2;
    const innerHeight = height - gap * 2;
    if (innerWidth > 0 && innerHeight > 0) {
      const inner = makeFrameStrip(x + gap, y + gap, innerWidth, innerHeight);
      setFrameBorder(inner, `${frame.thickness}px solid ${frame.color}`);
      nodes.push(inner);
    }
    const diamondSize = frame.thickness * 2.4;
    const corners: Array<[number, number]> = [
      [x, y],
      [x + width, y],
      [x, y + height],
      [x + width, y + height],
    ];
    corners.forEach(([cx, cy]) => {
      const diamond = makeFrameStrip(cx - diamondSize / 2, cy - diamondSize / 2, diamondSize, diamondSize);
      diamond.style.backgroundColor = frame.color;
      diamond.style.transform = 'rotate(45deg)';
      nodes.push(diamond);
    });
    return nodes;
  }

  if (frame.style === 'rustic') {
    const outerStroke = frame.thickness * 1.8;
    const cornerRadius = frame.thickness * 1.5;
    const outer = makeFrameStrip(x, y, width, height);
    setFrameBorder(outer, `${outerStroke}px dashed ${frame.color}`);
    outer.style.borderRadius = `${cornerRadius}px`;
    const nodes = [outer];
    const innerInset = outerStroke + frame.thickness;
    const innerWidth = width - innerInset * 2;
    const innerHeight = height - innerInset * 2;
    if (innerWidth > 0 && innerHeight > 0) {
      const inner = makeFrameStrip(x + innerInset, y + innerInset, innerWidth, innerHeight);
      setFrameBorder(inner, `${Math.max(1, frame.thickness * 0.5)}px solid ${frame.color}`);
      nodes.push(inner);
    }
    return nodes;
  }

  const div = makeFrameStrip(x, y, width, height);
  setFrameBorder(div, `${frame.thickness}px ${FRAME_CSS_BORDER_STYLE[frame.style] ?? 'solid'} ${frame.color}`);
  div.style.borderRadius = frame.style === 'rounded' ? `${frame.cornerRadius}px` : '0';
  return [div];
}

/**
 * Draws the frame around the top panel and its mirror image around the bottom panel (reflected
 * across the fold line), matching the mirroring the place card designer applies to elements.
 */
function appendFrameDivs(
  cardElement: HTMLDivElement,
  frame: PlaceCardFrame,
  cardWidthPx: number,
  foldLineYPx: number
): void {
  const frameWidth = cardWidthPx - frame.padding * 2;
  const frameHeight = foldLineYPx - frame.padding * 2;
  if (frameWidth <= 0 || frameHeight <= 0) {
    return;
  }

  const topX = frame.padding + frame.offsetX;
  const topY = frame.padding + frame.offsetY;
  const bottomY = 2 * foldLineYPx - (topY + frameHeight);

  const topRect: FrameCssRect = { x: topX, y: topY, width: frameWidth, height: frameHeight };
  const bottomRect: FrameCssRect = { x: topX, y: bottomY, width: frameWidth, height: frameHeight };

  [topRect, bottomRect].forEach((rect) => {
    buildFrameNodesForRect(rect, frame).forEach((node) => cardElement.appendChild(node));
  });
}

function pickTemplateElements(template: PlaceCardTemplate): PlaceCardElement[] {
  const baseElements =
    template.elements && template.elements.length > 0
      ? template.elements
      : convertLegacyFieldsToElements(template.fields);

  return baseElements.map((element, index) => ({
    ...element,
    id: element.id ?? `element-${index}`,
  }));
}

async function fetchUnitTypeRanks(unitId: string): Promise<Rank[]> {
  try {
    // First get the unit to find its typeId
    const unitRef = doc(db, 'units', unitId);
    const unitSnap = await getDoc(unitRef);
    
    if (!unitSnap.exists()) {
      console.warn('Unit not found:', unitId);
      return [];
    }

    const unitData = unitSnap.data();
    const typeId = unitData.typeId;

    if (!typeId) {
      console.warn('Unit has no typeId:', unitId);
      return [];
    }

    // Get unit type from system settings
    const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
    if (!settingsDoc.exists()) {
      console.warn('System settings not found');
      return [];
    }

    const settingsData = settingsDoc.data();
    const unitTypes: UnitType[] = settingsData.unitTypes || [];
    const unitType = unitTypes.find((ut) => ut.id === typeId);

    if (unitType && unitType.ranks) {
      // Sort ranks by order
      return [...unitType.ranks].sort((a, b) => a.order - b.order);
    }

    return [];
  } catch (error) {
    console.error('Error fetching unit type ranks:', error);
    return [];
  }
}

export interface CaptureElementToPngOptions {
  /** Target DPI for the output image (default 300). Use 600 for extra-sharp print. */
  targetDpi?: number;
  /** JPEG is much smaller than PNG and helps avoid jsPDF string length limits for large runs. */
  imageMime?: 'image/png' | 'image/jpeg';
  /** Quality for JPEG only (0–1). */
  jpegQuality?: number;
}

export async function captureElementToPng(
  element: HTMLElement,
  pixelWidth: number,
  pixelHeight: number,
  options: CaptureElementToPngOptions = {}
): Promise<string> {
  const dpi = options.targetDpi ?? TARGET_DPI;
  const renderScale = dpi / CSS_DPI;

  const canvas = await html2canvas(element, {
    width: pixelWidth,
    height: pixelHeight,
    scale: renderScale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    onclone: (clonedDoc: Document) => {
      // html2canvas v1.x reads computed CSS from the page and can render a hairline
      // border on every element because Tailwind Preflight sets border-style: solid
      // globally. Inject a stylesheet that hard-resets all borders in the clone,
      // except elements explicitly marked as needing a border (data-keep-border).
      const style = clonedDoc.createElement('style');
      style.textContent = [
        '* { border-style: none !important; outline: none !important;',
        '    box-shadow: none !important; }',
        '[data-keep-border] { border-style: solid !important; }',
      ].join(' ');
      clonedDoc.head.appendChild(style);
    },
  });

  const mime = options.imageMime ?? 'image/png';
  if (mime === 'image/jpeg') {
    return canvas.toDataURL('image/jpeg', options.jpegQuality ?? 0.9);
  }
  return canvas.toDataURL('image/png');
}

/** jsPDF builds the file with Array.join; too many large base64 images exceeds the max string length. */
const MAX_PLACE_CARDS_PER_PDF_PART = 28;

function sanitizePlaceCardDownloadFileName(name: string): string {
  const s = name.replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_').replace(/\s+/g, ' ').trim();
  if (!s) {
    return 'Event';
  }
  return s.length > 100 ? `${s.slice(0, 97)}...` : s;
}

function getTableNameFromTable(table: SeatingTable): string {
  if (table.name) {
    return table.name;
  }
  return `Table ${table.id}`;
}

export interface PlaceCardGenerationResult {
  totalCards: number;
  /** Cards that could not be rendered and were left blank in the PDF. */
  failedCards: number;
  /** Cards on which at least one image could not be loaded. */
  cardsWithMissingImages: number;
}

export async function generateCustomPlaceCards(
  rsvps: RSVP[],
  event: Event,
  tables: SeatingTable[],
  template: PlaceCardTemplate,
  unitInfo?: { name?: string; number?: string; logoUrl?: string }
): Promise<PlaceCardGenerationResult> {
  if (!rsvps || rsvps.length === 0) {
    throw new Error('No RSVPs provided for place cards generation');
  }

  if (!event) {
    throw new Error('No event provided for place cards generation');
  }

  // Normalize paper size to jsPDF format tokens
  const normalizePaperSize = (value: string | undefined): 'a4' | 'a5' | 'letter' | 'legal' => {
    const lower = (value || 'A4').toString().trim().toLowerCase();
    if (lower === 'a5') return 'a5';
    if (lower === 'letter') return 'letter';
    if (lower === 'legal') return 'legal';
    return 'a4';
  };

  const seatingMap = buildPlaceCardSeatingMap(tables);

  const { width: cardWidthInches, height: cardHeightInches } = templateCardSizeInches(template);

  const elements = pickTemplateElements(template);
  // Make sure web fonts are ready before html2canvas measures and paints the text
  await ensurePlaceCardFontsLoaded(elements);
  // Always mirror around centre for PDF output
  const foldRatio = 0.5;

  // Fetch unit type ranks for prefix lookup
  const ranks = event.unitId ? await fetchUnitTypeRanks(event.unitId) : [];

  const orderedRsvps = sortRsvpsBySeating(rsvps, tables);
  const totalCards = orderedRsvps.length;

  const captureOptions: CaptureElementToPngOptions =
    totalCards >= 24
      ? {
          targetDpi: totalCards > 72 ? 190 : totalCards > 48 ? 210 : TARGET_DPI,
          imageMime: 'image/jpeg',
          jpegQuality: 0.91,
        }
      : { targetDpi: TARGET_DPI };
  const pdfRasterFormat: 'PNG' | 'JPEG' =
    captureOptions.imageMime === 'image/jpeg' ? 'JPEG' : 'PNG';

  let failedCards = 0;
  let cardsWithMissingImages = 0;

  const partSlices: RSVP[][] = [];
  for (let i = 0; i < orderedRsvps.length; i += MAX_PLACE_CARDS_PER_PDF_PART) {
    partSlices.push(orderedRsvps.slice(i, i + MAX_PLACE_CARDS_PER_PDF_PART));
  }

  for (let partIdx = 0; partIdx < partSlices.length; partIdx++) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: normalizePaperSize(template.paperSize as unknown as string),
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const cardsPerRow = cardsThatFit(pageWidth, cardWidthInches);
    const cardsPerCol = cardsThatFit(pageHeight, cardHeightInches);
    const maxCardsPerPage = Math.max(1, cardsPerRow * cardsPerCol);
    const cardsPerPage = Math.min(template.cardsPerPage || maxCardsPerPage, maxCardsPerPage);

    let cardCount = 0;

    for (const rsvp of partSlices[partIdx]) {
    if (cardCount > 0 && cardCount % cardsPerPage === 0) {
      doc.addPage();
    }

    const position = cardCount % cardsPerPage;
    const row = Math.floor(position / cardsPerRow);
    const col = position % cardsPerRow;

    // Center the grid of cards on the page (prevents uneven margins and matches canvas). A grid
    // that overshoots the sheet slightly (the 8.3in A5 card on 8.27in A5 paper) is scaled down
    // uniformly so nothing is cropped.
    const gridScale = Math.min(
      1,
      pageWidth / (cardsPerRow * cardWidthInches),
      pageHeight / (cardsPerCol * cardHeightInches)
    );
    const printedCardWidth = cardWidthInches * gridScale;
    const printedCardHeight = cardHeightInches * gridScale;
    const marginX = Math.max(0, (pageWidth - cardsPerRow * printedCardWidth) / 2);
    const marginY = Math.max(0, (pageHeight - cardsPerCol * printedCardHeight) / 2);
    const xOffset = marginX + col * printedCardWidth;
    const yOffset = marginY + row * printedCardHeight;

    const cardElement = document.createElement('div');
    // Render at CSS 96 DPI so sizes match editor preview
    cardElement.style.width = `${cardWidthInches * 96}px`;
    cardElement.style.height = `${cardHeightInches * 96}px`;
    // Take it out of normal document flow and move it off-screen so appending/removing
    // one of these per guest doesn't grow the page and cause the whole page to visibly
    // shift (e.g. scrollbars appearing/disappearing) while the PDF is being generated.
    cardElement.style.position = 'fixed';
    cardElement.style.top = '0';
    cardElement.style.left = '-99999px';
    cardElement.style.pointerEvents = 'none';
    cardElement.style.backgroundColor = '#ffffff';
    // Explicitly reset borders — html2canvas reads computed CSS and can render a 1px border
    // from Tailwind Preflight (border-style: solid) combined with the global border-color rule
    cardElement.style.border = 'none';
    cardElement.style.outline = 'none';
    cardElement.style.boxShadow = 'none';
    cardElement.style.fontFamily = 'Georgia, serif';
    cardElement.style.overflow = 'hidden';
    cardElement.style.boxSizing = 'border-box';

    let cardHasMissingImage = false;
    const placeholderValues = buildPlaceholderMap(rsvp, seatingMap.get(rsvp.id), event, unitInfo ?? {}, ranks);

    for (const element of elements) {
      const xRatio = clampValue(element.x ?? 0, 0, 1);
      const yRatio = clampValue(element.y ?? 0, 0, 1);
      const widthRatio = clampValue(element.width ?? 0.2, 0, 1);
      const heightRatio = clampValue(element.height ?? 0.1, 0, 1);

      const elementDiv = document.createElement('div');
      elementDiv.style.position = 'absolute';
      elementDiv.style.left = `${xRatio * 100}%`;
      elementDiv.style.top = `${yRatio * 100}%`;
      elementDiv.style.width = `${widthRatio * 100}%`;
      elementDiv.style.height = `${heightRatio * 100}%`;
      elementDiv.style.display = 'flex';
      elementDiv.style.alignItems = 'center';
      const align = element.textAlign ?? 'center';
      elementDiv.style.justifyContent =
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      elementDiv.style.textAlign = align;
      elementDiv.style.padding = '4px';
      elementDiv.style.boxSizing = 'border-box';
      elementDiv.style.pointerEvents = 'none';
      elementDiv.style.whiteSpace = 'pre-wrap';
      elementDiv.style.color = element.color ?? '#111827';
      elementDiv.style.opacity = (element.opacity ?? 1).toString();
      elementDiv.style.transform = `rotate(${element.rotation ?? 0}deg)`;
      elementDiv.style.transformOrigin = 'center center';
      elementDiv.style.lineHeight = element.lineHeight ? String(element.lineHeight) : '1.2';
      if (element.letterSpacing !== undefined) {
        elementDiv.style.letterSpacing = `${element.letterSpacing}px`;
      }
      if (element.backgroundColor) {
        elementDiv.style.backgroundColor = element.backgroundColor;
      }
      if (element.borderRadius) {
        elementDiv.style.borderRadius = `${element.borderRadius}px`;
      }
      // Respect element stroke only if explicitly configured by template
      if (element.strokeColor && element.strokeWidth) {
        elementDiv.style.border = `${element.strokeWidth}px solid ${element.strokeColor}`;
        elementDiv.dataset.keepBorder = 'true';
      } else {
        elementDiv.style.border = 'none';
        elementDiv.style.outline = 'none';
        elementDiv.style.boxShadow = 'none';
      }

      if (element.type === 'text') {
        const fontSizePx = (element.fontSize ?? 24);
        elementDiv.style.fontSize = `${fontSizePx}px`;
        elementDiv.style.fontFamily = element.fontFamily ?? 'Georgia, serif';
        elementDiv.style.fontWeight = cssFontWeight(element.fontWeight);
        if (element.fontStyle) {
          elementDiv.style.fontStyle = element.fontStyle;
        } else {
          elementDiv.style.fontStyle = 'normal';
        }
        if (element.textDecoration) {
          elementDiv.style.textDecoration = element.textDecoration;
        } else {
          elementDiv.style.textDecoration = 'none';
        }
        elementDiv.textContent = applyPlaceholders(element.content ?? '', placeholderValues);
      } else if (element.type === 'image') {
        // Images must fill the container without any padding — clear it to avoid a border-like gap
        elementDiv.style.padding = '0';
        // Unit logo elements always use the unit's current logo (the stored URL may be stale)
        const imgSrc =
          element.imageType === 'unit-logo' && unitInfo
            ? unitInfo.logoUrl ?? ''
            : element.imageUrl ?? unitInfo?.logoUrl ?? '';
        if (imgSrc) {
          const img = document.createElement('img');
          img.src = imgSrc;
          img.crossOrigin = 'anonymous';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.display = 'block';
          img.style.border = 'none';
          img.style.outline = 'none';
          img.style.boxShadow = 'none';
          // Use 'fill' so the image is drawn at exactly the container's integer pixel dimensions.
          // 'contain' computes a non-integer scale factor when the natural image size doesn't
          // match the container, producing a 1px anti-aliased halo that html2canvas captures.
          img.style.objectFit = 'fill';
          elementDiv.appendChild(img);
          const loaded = await new Promise<boolean>((resolve) => {
            if (img.complete && img.naturalWidth) {
              resolve(true);
            } else {
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
            }
          });
          if (!loaded) {
            cardHasMissingImage = true;
            img.remove();
          }
        }
      }

      cardElement.appendChild(elementDiv);

      if (element.mirrored) {
        const mirroredDiv = elementDiv.cloneNode(true) as HTMLDivElement;
        const elementHeightRatio = heightRatio;
        const centerRatio = yRatio + elementHeightRatio / 2;
        const mirroredCenter = 2 * foldRatio - centerRatio;
        const mirroredTop = clampValue(
          mirroredCenter - elementHeightRatio / 2,
          0,
          1 - elementHeightRatio
        );
        mirroredDiv.style.top = `${mirroredTop * 100}%`;
        const mirroredRotation = (element.rotation ?? 0) + (element.mirrorRotation ?? 180);
        mirroredDiv.style.transform = `rotate(${mirroredRotation}deg)`;
        cardElement.appendChild(mirroredDiv);
      }
    }

    const frame = template.layout?.frame;
    if (frame?.enabled) {
      appendFrameDivs(cardElement, frame, cardWidthInches * 96, cardHeightInches * 96 * foldRatio);
    }

    document.body.appendChild(cardElement);

    try {
      // Render at higher DPI for sharper text/images in PDF using shared helper
      const imgData = await captureElementToPng(
        cardElement,
        cardWidthInches * CSS_DPI,
        cardHeightInches * CSS_DPI,
        captureOptions
      );
      doc.addImage(imgData, pdfRasterFormat, xOffset, yOffset, printedCardWidth, printedCardHeight);
      if (cardHasMissingImage) {
        cardsWithMissingImages++;
      }
    } catch (error) {
      console.error('Error generating card image:', error);
      failedCards++;
    } finally {
      if (document.body.contains(cardElement)) {
        document.body.removeChild(cardElement);
      }
    }

    cardCount++;
    }

    const safeName = sanitizePlaceCardDownloadFileName(event.name);
    const partSuffix =
      partSlices.length > 1 ? ` (Part ${partIdx + 1} of ${partSlices.length})` : '';
    doc.save(`${safeName} - Custom Place Cards${partSuffix}.pdf`);

    if (partIdx < partSlices.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }

  return { totalCards, failedCards, cardsWithMissingImages };
}

/** User-facing warning for a partially failed place card run, or null when every card rendered fully. */
export function describePlaceCardGenerationProblems(result: PlaceCardGenerationResult): string | null {
  const problems: string[] = [];
  if (result.failedCards > 0) {
    problems.push(
      `${result.failedCards} of ${result.totalCards} card${result.totalCards === 1 ? '' : 's'} could not be rendered and ${result.failedCards === 1 ? 'is' : 'are'} blank`
    );
  }
  if (result.cardsWithMissingImages > 0) {
    problems.push(
      `${result.cardsWithMissingImages} card${result.cardsWithMissingImages === 1 ? ' is' : 's are'} missing an image that failed to load`
    );
  }
  return problems.length > 0 ? `${problems.join('; ')}. Check the images in the template and try again.` : null;
}


